import type { ConfigurationSchema } from 'src/configuration.types'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import { PropertyEvaluatorClass } from 'src/services/mappings/PropertyEvaluatorClass/PropertyEvaluatorClass'
import type {
  ConditionCallback,
  SchemaFieldStatus,
  SchemaPropertyInterface,
} from 'src/services/mappings/jsonSchema/jsonSchema.types'
import type { JsonSchemaPropertyByFieldKey } from 'src/services/mappings/jsonSchema/jsonSchema.types2'
import {
  FIELD_RULES,
  FIELD_STATUSES,
} from 'src/tpbp-core/models/fields/fieldRules.const'
import { scalarToString, zipObject } from 'src/tpbp-core/utils/common.utils'
import { logger } from 'src/utils/logger.utils'

export function getConditionCallback(
  propertyKey: string,
  property: SchemaPropertyInterface,
): ConditionCallback | undefined {
  if (property.enum) {
    const authorizedValuesMap = zipObject(property.enum, true)

    return (record, fieldStatusesByOriginKey) => {
      const status = Object.hasOwn(fieldStatusesByOriginKey, propertyKey)
        ? fieldStatusesByOriginKey[propertyKey]
        : FIELD_STATUSES.isNotInSchema
      const value =
        status === FIELD_STATUSES.isNotInSchema
          ? undefined
          : record.$attributes[propertyKey]

      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        const stringValue = scalarToString(value)

        return Boolean(authorizedValuesMap[stringValue])
      }
      return false
    }
  }
  if (property.not) {
    // eslint-disable-next-line consistent-return
    return
  }

  throw new Error(`Unmanaged case property '${JSON.stringify(property)}'`)
}

function getEvaluatorClass(evaluatorClassesByProperty, propertyKey) {
  if (!Object.hasOwn(evaluatorClassesByProperty, propertyKey)) {
    evaluatorClassesByProperty[propertyKey] = new PropertyEvaluatorClass(
      propertyKey,
    )
  }

  return evaluatorClassesByProperty[propertyKey]
}

function addProperties({
  conditionCb,
  evaluatorClassesByProperty,
  status,
  propertyKey,
}: {
  conditionCb: ConditionCallback
  evaluatorClassesByProperty: any
  status: SchemaFieldStatus
  propertyKey: string
}) {
  const evaluatorClass = getEvaluatorClass(
    evaluatorClassesByProperty,
    propertyKey,
  )
  const enums = null

  evaluatorClass.appendCondition({ conditionCb, status, enums })
}

export function mapDependencyProperty(
  evaluatorClassesByProperty,
  dependencyProperty,
) {
  const propertiesByKey = dependencyProperty.properties
  const propertyKeys = Object.keys(dependencyProperty.properties)
  const { required } = dependencyProperty
  const firstPropertyKey = propertyKeys.splice(0, 1)[0]
  if (firstPropertyKey === undefined) {
    throw new Error('Expecting at least one property')
  }
  const firstProperty = propertiesByKey[firstPropertyKey]

  const conditionCallback = getConditionCallback(
    firstPropertyKey,
    firstProperty,
  )
  if (!conditionCallback) {
    return
  }

  for (const propertyKey of propertyKeys) {
    const status = getSchemaFieldStatus(propertyKey, required)

    addProperties({
      conditionCb: conditionCallback,
      evaluatorClassesByProperty,
      status,
      propertyKey,
    })
  }
}

export function evalDependencies(evaluatorClassesByProperty, dependencies) {
  if (!dependencies) {
    return
  }
  for (const dependencyKey of Object.keys(dependencies)) {
    const dependency = dependencies[dependencyKey]
    const dependencyTypes = Object.keys(dependency)
    if (dependencyTypes.length !== 1) {
      throw new Error('Expecting only one dependency type')
    }
    if (dependencyTypes[0] !== 'oneOf') {
      throw new Error("Expecting 'oneOf' dependency type")
    }

    for (const row of dependency.oneOf) {
      mapDependencyProperty(evaluatorClassesByProperty, row)
    }
  }
}

function conditionCallbackAlways() {
  return true
}

function getSchemaFieldStatus(
  propertyKey: string,
  requiredKeys: string[],
): SchemaFieldStatus {
  return requiredKeys.includes(propertyKey)
    ? FIELD_RULES.isMandatory
    : FIELD_RULES.isOptional
}

export function mapSchema(
  schema: ConfigurationSchema,
): JsonSchemaPropertyByFieldKey {
  const evaluatorClassesByProperty = {}
  const {
    properties: propertiesByKey,
    required,
    dependencies,
    disabled_if_present: disabledIfPresent,
  } = schema
  const propertyKeys = Object.keys(propertiesByKey)

  const disabledIfPresentFieldKeys = disabledIfPresent || []

  for (const propertyKey of propertyKeys) {
    const isDisabledIfPresent = disabledIfPresentFieldKeys.includes(propertyKey)

    if (isDisabledIfPresent) {
      const conditionCallbackIfPresent = (record) =>
        `${record.$getOriginValue(propertyKey) ?? ''}` !== ''

      addProperties({
        conditionCb: conditionCallbackIfPresent,
        evaluatorClassesByProperty,
        status: FIELD_RULES.isReadOnly,
        propertyKey,
      })
    }

    const status = getSchemaFieldStatus(propertyKey, required)
    addProperties({
      conditionCb: conditionCallbackAlways,
      evaluatorClassesByProperty,
      status,
      propertyKey,
    })
  }
  evalDependencies(evaluatorClassesByProperty, dependencies)

  return evaluatorClassesByProperty
}

function getPropertyIsValid(
  property: SchemaPropertyInterface | undefined,
  value: unknown,
): boolean {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    const index = property?.enum ? property.enum.indexOf(value) : -1

    return index >= 0
  }
  return false
}

export function getDependencyIsValid(
  properties: Record<string, SchemaPropertyInterface | undefined> | undefined,
  record: ProxyRecord,
): boolean {
  const keys = Object.keys(properties ?? {})
  if (keys.length === 0) {
    logger.warn('properties should not be empty')
    return false
  }
  const key = keys[0]
  if (key === undefined) {
    throw new Error('failed to find first key')
  }
  const value = record.$attributes[key]
  const property = properties?.[key]

  return getPropertyIsValid(property, value)
}

export function getFirstValidDependencyIndex(
  dependencies: Record<
    string,
    Record<string, SchemaPropertyInterface | undefined>
  >[],
  record: ProxyRecord,
) {
  const index = dependencies.findIndex((dependency) =>
    getDependencyIsValid(dependency.properties, record),
  )

  return index
}

export function getDependencyFieldRules(dependencies) {
  const fields = Object.keys(dependencies.properties).reduce(
    (fields, key, index) => {
      if (index > 0) {
        const property = dependencies.properties[key]

        fields[key] = { isRequired: false, ...property }
      }

      return fields
    },
    {},
  )

  if (dependencies.required) {
    // add required fields
    for (const requiredField of dependencies.required as string[]) {
      if (Object.hasOwn(fields, requiredField)) {
        fields[requiredField].isRequired = true
      } else {
        fields[requiredField] = {
          isRequired: true,
        }
      }
    }
  }

  return fields
}

export function getDependenciesFieldRules(
  dependencies: Record<string, unknown>,
  record: ProxyRecord,
) {
  if (!dependencies) {
    return {}
  }
  const keys = Object.keys(dependencies)
  // eslint-disable-next-line unicorn/no-array-reduce
  const fieldRules = keys.reduce((fieldRules, key) => {
    const dependency = dependencies[key] as Record<
      'oneOf',
      Record<string, Record<string, SchemaPropertyInterface | undefined>>[]
    >
    const { oneOf } = dependency
    const index = getFirstValidDependencyIndex(oneOf, record)
    if (index >= 0) {
      const dependencyFieldRules = getDependencyFieldRules(oneOf[index])
      Object.assign(fieldRules, dependencyFieldRules)
    }

    return fieldRules
  }, {})

  return fieldRules
}
